import {
  Component,
  ContentChild,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
} from '@angular/core';

import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { ManagedFile } from '@app/framework/interfaces/file.type';
import { NotificationsService } from '@app/services/notifications.service';
import { UploadHiddenInputComponent } from '@app/framework/file-handling/upload-hidden-input/upload-hidden-input.component';
import { DefaultUploadUiComponent } from '@app/framework/file-handling/default-upload-ui/default-upload-ui.component';

/**
 * General upload component used for displaying and storing files locally in a hidden file upload input. <br/>
 * Files can be added by clicking the element or dropping files on it. <br/>
 * File uploads need to be handled outside of this component.
 */
@Component({
  selector: 'app-input-upload',
  imports: [MatTooltipModule, UploadHiddenInputComponent, DefaultUploadUiComponent],
  templateUrl: './input-upload.component.html',
})
export class InputUploadComponent implements OnInit, OnDestroy {
  private notif = inject(NotificationsService);

  @ContentChild(TemplateRef) projectedContentTemplate: TemplateRef<any>;
  @Input() disableUpload = false;
  @Input() hideRemoveFileIcon = false;
  @Input() files: ManagedFile[] = [];
  @Input() allowMultipleFiles = false;
  @Output() filesChanged = new EventEmitter<ManagedFile[]>();
  @Output() removeFile = new EventEmitter<{ file: ManagedFile; index: number }>();
  @Input() placeholder = 'Click to upload files';
  @Input() label = '';
  private isDestroyed$ = new Subject();

  ngOnInit() {}

  ngOnDestroy() {
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }

  /**
   * Handles file change event, emits files to parent component
   * Parent has to handle the files as needed in its context and send back the current state of files to this component
   * (e.g. current files + local files or just local files)
   * @param files - chosen files to be uploaded
   */
  onFileInputChange(files: (File | ManagedFile)[]) {
    this.filesChanged.emit([...(this.files ?? []), ...files]);
    // TBD this file appending
    // this.files = [...this.files, files];
  }

  /**
   * Add files to upload queue from drag and drop event
   * @param droppedFiles
   */
  onFileDropped(droppedFiles: File[]) {
    const files = Array.from(droppedFiles);
    if (files.length > 1 && !this.allowMultipleFiles) {
      this.notif.showError('You can only upload one file in this section');
      return;
    }
    this.filesChanged.emit([...(this.files ?? []), ...files]);
  }

  onRemoveFile({ file, index }: { file: File | any; index: number }) {
    this.removeFile.emit({ file, index });
  }
}
